import { Job, SupabaseJob } from '@applicator/shared'
import { useState, useEffect, useRef } from 'react'

import { getStatusBadgeClasses, getStatusDisplayText, getRelevanceBadgeClasses, getRelevanceBadgeText, shouldShowRelevanceBadge } from './badges'
import { useTooltipHandlers } from './tooltips'

interface JobListItemProps {
  job: Job
  index?: number
  isSelected: boolean
  onSelect: () => void
  supabaseJob?: SupabaseJob
  itemRef?: (el: HTMLDivElement | null) => void
}

function JobListItem({ 
  job, 
  isSelected, 
  onSelect,
  supabaseJob,
  itemRef
}: JobListItemProps) {
  const positionRef = useRef<HTMLSpanElement>(null)
  const companyRef = useRef<HTMLSpanElement>(null)
  const [showPositionTooltip, setShowPositionTooltip] = useState(false)
  const [showCompanyTooltip, setShowCompanyTooltip] = useState(false)
  const [positionTooltipPos, setPositionTooltipPos] = useState<{ top: number; left: number } | null>(null)
  const [companyTooltipPos, setCompanyTooltipPos] = useState<{ top: number; left: number } | null>(null)
  const positionTooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const companyTooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const {
    hideAllTooltips,
    createTooltipMouseEnterHandler,
    createTooltipMouseLeaveHandler,
    createTooltipMouseMoveHandler,
    setupTruncationListeners,
    cleanupTruncationListeners,
    checkTruncationForBothElements
  } = useTooltipHandlers({
    positionRef,
    companyRef,
    setShowPositionTooltip,
    setShowCompanyTooltip,
    setPositionTooltipPos,
    setCompanyTooltipPos,
    positionTooltipTimeoutRef,
    companyTooltipTimeoutRef
  })

  useEffect(() => {
    const { timeoutIds, resizeObserver, checkTruncation } = setupTruncationListeners(checkTruncationForBothElements)
    
    return () => {
      cleanupTruncationListeners(timeoutIds, resizeObserver, checkTruncation)
    }
  }, [job.position, job.company, showPositionTooltip, showCompanyTooltip, setupTruncationListeners, cleanupTruncationListeners, checkTruncationForBothElements])

  return (
    <div
      ref={(el) => {
        if (itemRef) {
          itemRef(el)
        }
      }}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
      onMouseLeave={hideAllTooltips}
      className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
        isSelected
          ? 'bg-brand-primary-subtle border border-brand-primary/20 shadow-sm'
          : 'border border-transparent hover:bg-brand-primary-subtle hover:border-brand-primary/20'
      }`}
    >
      <div 
        className="font-medium text-sm text-text-primary mb-1 relative group/title"
      >
        <span 
          ref={positionRef}
          className="block truncate"
          onMouseEnter={createTooltipMouseEnterHandler(
            positionRef,
            positionTooltipTimeoutRef,
            setShowPositionTooltip,
            setPositionTooltipPos
          )}
          onMouseLeave={createTooltipMouseLeaveHandler(
            positionTooltipTimeoutRef,
            setShowPositionTooltip,
            setPositionTooltipPos
          )}
          onMouseMove={createTooltipMouseMoveHandler(
            positionRef,
            showPositionTooltip,
            setPositionTooltipPos
          )}
          onBlur={createTooltipMouseLeaveHandler(
            positionTooltipTimeoutRef,
            setShowPositionTooltip,
            setPositionTooltipPos
          )}
        >
          {job.position}
        </span>
        {showPositionTooltip && positionTooltipPos && (
          <div 
            className="fixed z-[9999] bg-text-primary text-text-inverse text-xs rounded py-1.5 px-2.5 whitespace-nowrap shadow-lg pointer-events-none"
            style={{
              top: `${positionTooltipPos.top}px`,
              left: `${positionTooltipPos.left}px`,
              transform: 'translateY(-100%)',
              maxWidth: '90vw',
            }}
          >
            {job.position}
            <div className="absolute left-3 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-text-primary"></div>
          </div>
        )}
      </div>
      <div 
        className="text-xs text-text-muted relative group/company flex items-center gap-2 flex-wrap"
      >
        <span 
          ref={companyRef}
          className="block truncate flex-1 min-w-0"
          onMouseEnter={createTooltipMouseEnterHandler(
            companyRef,
            companyTooltipTimeoutRef,
            setShowCompanyTooltip,
            setCompanyTooltipPos
          )}
          onMouseLeave={createTooltipMouseLeaveHandler(
            companyTooltipTimeoutRef,
            setShowCompanyTooltip,
            setCompanyTooltipPos
          )}
          onMouseMove={createTooltipMouseMoveHandler(
            companyRef,
            showCompanyTooltip,
            setCompanyTooltipPos
          )}
          onBlur={createTooltipMouseLeaveHandler(
            companyTooltipTimeoutRef,
            setShowCompanyTooltip,
            setCompanyTooltipPos
          )}
        >
          {job.company}
        </span>
        {job.status && (
          <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap flex-shrink-0 ${getStatusBadgeClasses(job.status)}`}>
            {getStatusDisplayText(job.status)}
          </span>
        )}
        {shouldShowRelevanceBadge(supabaseJob) && (
          <span className={getRelevanceBadgeClasses(supabaseJob)}>
            {getRelevanceBadgeText(supabaseJob)}
          </span>
        )}
        {showCompanyTooltip && companyTooltipPos && (
          <div 
            className="fixed z-[9999] bg-text-primary text-text-inverse text-xs rounded py-1.5 px-2.5 whitespace-nowrap shadow-lg pointer-events-none"
            style={{
              top: `${companyTooltipPos.top}px`,
              left: `${companyTooltipPos.left}px`,
              transform: 'translateY(-100%)',
              maxWidth: '90vw',
            }}
          >
            {job.company}
            <div className="absolute left-3 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-text-primary"></div>
          </div>
        )}
      </div>
    </div>
  )
}

export default JobListItem
